import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProductAccountDTO } from 'api-models'
import { Onboarding } from 'services/user'
import { GiPartyPopper } from 'react-icons/gi'
import { useStores } from 'hooks'
import { GButton } from 'components/basic-blocks'
import { DotMatrix, AppButton } from '@clickguard/design-system'
import { GDialog } from 'components/basic-blocks/g-dialog'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import ProgressCircle from 'components/circle-progress-bar/circle-progress-bar'
import { getOnboardingBookingUrl } from 'utils/hubspot'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'

interface SetupV2BannerProps {
	account: ProductAccountDTO
	assetId?: string
	websiteStep?: '2' | '3' | 'SETUP_COMPLETE'
}

export const SetupV2Banner = ({
	account,
	assetId,
	websiteStep
}: SetupV2BannerProps) => {
	const [isOptingOut, setIsOptingOut] = useState(false)
	const [showCompletionModal, setShowCompletionModal] = useState(false)
	const navigate = useNavigate()
	const { assetStore } = useStores()
	const { allAssets } = assetStore
	const { adAccounts } = useAssetStore(
		useShallow((s) => ({
			adAccounts: s.adAccounts
		}))
	)
	const hasAdAccountInAsset = adAccounts.length > 0
	const trackPostHogEvent = useTrackPostHogEvent()

	const setup = account.setup
	if (!setup) return null

	const step = setup.step as string

	// Determine step completion status (same logic as setup sidebar)
	const isStep1Done =
		step === 'WEBSITE_PROTECTED' ||
		step === 'AD_ACCOUNT_CONNECTED' ||
		step === 'SETUP_COMPLETE'
	const isStep2Done =
		websiteStep !== undefined
			? websiteStep === '3'
			: (step === 'AD_ACCOUNT_CONNECTED' || step === 'SETUP_COMPLETE') &&
			  hasAdAccountInAsset
	const isStep3Done =
		websiteStep !== undefined
			? websiteStep === 'SETUP_COMPLETE'
			: step === 'SETUP_COMPLETE'

	// Determine which step is currently active
	// Step 1 is active if not done
	// Step 2 is active if step 1 is done but step 2 is not
	// Step 3 is active if step 2 is done but step 3 is not
	const isStep1Active = !isStep1Done
	const isStep2Active = isStep1Done && !isStep2Done
	const isStep3Active = isStep2Done && !isStep3Done

	// Don't show banner if setup is complete
	if (isStep3Done) return null

	// Calculate remaining steps
	const remainingSteps = !isStep1Done ? 3 : !isStep2Done ? 2 : 1
	const titleText =
		remainingSteps === 1
			? 'Complete one more step to achieve full protection'
			: remainingSteps === 2
			? 'Complete two more steps to achieve full protection'
			: 'Complete three more steps to achieve full protection'

	// Determine which step to navigate to
	const getContinueUrl = () => {
		if (!isStep1Done) {
			// Navigate to step 1
			return `/setup/${account.id}`
		} else if (!isStep2Done) {
			// Navigate to step 2 - need to find asset
			if (assetId) {
				return `/setup/${account.id}/${assetId}`
			}
			return `/setup/${account.id}`
		} else {
			// Navigate to step 3
			if (assetId) {
				return `/setup/${account.id}/${assetId}/tracking`
			}
			return `/setup/${account.id}`
		}
	}

	const handleContinueSetup = () => {
		trackPostHogEvent(
			PostHogEvent.ONBOARDING_BANNER_CLICK_CONTINUE_SETUP,
			PostHogPath.ONBOARDING,
			null
		)
		navigate(getContinueUrl())
	}

	const handleOptOutTracking = async () => {
		setIsOptingOut(true)
		try {
			trackPostHogEvent(
				PostHogEvent.ONBOARDING_BANNER_SKIP_TC,
				PostHogPath.ONBOARDING,
				null
			)

			if (account.id && assetId) {
				await Onboarding.optOutTracking(account.id, assetId)
			}
			// Show completion modal before reload
			setIsOptingOut(false)
			setShowCompletionModal(true)
		} catch (error) {
			console.error('Failed to opt out of tracking code:', error)
			setIsOptingOut(false)
		}
	}

	const handleOpenDashboard = () => {
		window.location.reload()
	}

	// Prevent modal from closing - user must click button
	const handleModalClose = () => {
		// Modal cannot be closed - user must click "Open dashboard" button
	}

	// Check if we should show "Finish setup with an expert" button
	// Same logic as setup sidebar
	const shouldShowScheduleCall = () => {
		if (!account?.setup) return false
		const adSpend = account.setup.spend || 0
		const isHighSpend = adSpend >= 10000
		const isFirstWebsite = allAssets.length === 1
		return isHighSpend && isFirstWebsite
	}
	const showScheduleCall = shouldShowScheduleCall()

	return (
		<div className="w-full">
			{/* Completion Modal */}
			<GDialog
				open={showCompletionModal}
				onClose={handleModalClose}
				showClose={false}
				maxWidth="xl"
				className="p-8"
			>
				<div className="flex flex-col items-center text-center">
					{/* Title with icon */}
					<div className="flex items-start">
						<h2 className="text-2xl font-bold text-t-default">All set!</h2>
						<GiPartyPopper className="w-10 h-10" />
					</div>

					{/* Informational text */}
					<div className="mb-6">
						<p className="text-t-email-blue text-sm ">
							You can always install your tracking code later — it's recommended
							to maximize protection.
						</p>
						<p className="text-t-default text-sm leading-5">
							Your setup is complete. You can now explore your dashboard and
							track results.
						</p>
					</div>

					{/* Open dashboard button */}
					<AppButton
						size="md"
						fullWidth
						label="Open dashboard"
						onClick={handleOpenDashboard}
					/>
				</div>
			</GDialog>

			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex gap-3">
					<div className="flex items-center">
						<ProgressCircle step={3 - remainingSteps} />
					</div>
					<div className="flex-1">
						<h1 className="text-xl font-medium text-gray-900">{titleText}</h1>
						{remainingSteps > 1 && (
							<p className="text-t-gray-checkout text-[14px] leading-[20px] font-normal">
								This dashboard is showing demo data to get you started. Your
								real data will appear after setup is complete.
							</p>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2 ml-4">
					<AppButton
						size="md"
						label="Continue setup"
						onClick={handleContinueSetup}
					/>
				</div>
			</div>

			{/* Steps Section */}
			<hr className="my-4 border-t-badge-border" />
			<>
				<div className="flex items-center gap-8 mt-4">
					{/* Step 01: Set up detection */}
					<div className="flex items-center gap-3">
						<div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0">
							{isStep1Done ? (
								/* Completed: Yellow background with checkmark */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-b-yellow-tint border-2 border-t-email-blue flex items-center justify-center">
									<svg
										className="w-4 h-4 text-t-email-blue"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={3}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
							) : isStep1Active ? (
								/* Active: Purple gradient with home icon */
								<div
									className="w-[30px] h-[30px] rounded-[8px] border-2 border-[#554bfd] flex items-center justify-center"
									style={{
										backgroundImage:
											'linear-gradient(-86.7546542701692deg, rgba(145, 138, 255, 1) 3.8923%, rgba(94, 84, 253, 1) 93.118%)'
									}}
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_4734_43385)">
											<path
												d="M7.33301 7.33333H4.13301C3.38627 7.33333 3.0129 7.33333 2.72769 7.47866C2.4768 7.60649 2.27283 7.81046 2.145 8.06135C1.99967 8.34656 1.99967 8.71993 1.99967 9.46667V14M13.9997 14V4.13333C13.9997 3.3866 13.9997 3.01323 13.8543 2.72801C13.7265 2.47713 13.5225 2.27316 13.2717 2.14532C12.9864 2 12.6131 2 11.8663 2H9.46634C8.7196 2 8.34624 2 8.06102 2.14532C7.81014 2.27316 7.60616 2.47713 7.47833 2.72801C7.33301 3.01323 7.33301 3.3866 7.33301 4.13333V14M14.6663 14H1.33301M9.66634 4.66667H11.6663M9.66634 7.33333H11.6663M9.66634 10H11.6663"
												stroke="white"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_4734_43385">
												<rect width="16" height="16" fill="white" />
											</clipPath>
										</defs>
									</svg>
								</div>
							) : (
								/* Inactive: White square with purple border and home icon */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-t-step-inactive border border-t-badge-border flex items-center justify-center">
									<svg
										className="w-4 h-4 text-t-email-blue"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
										/>
									</svg>
								</div>
							)}
						</div>
						<div className="flex flex-col gap-[2px]">
							<p className="text-t-email-blue text-[12px] leading-[14px] font-bold">
								Step 01
							</p>
							<p
								className={`text-t-default font-normal text-[14px] leading-[16px] opacity-85 ${
									isStep1Done ? 'line-through' : ''
								}`}
							>
								Set up <span className="font-bold">detection</span>
							</p>
						</div>
					</div>

					{/* Step 02: Connect your Ad account */}
					<div className="flex items-center gap-3">
						<div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0">
							{isStep2Done ? (
								/* Completed: Yellow background with checkmark */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-b-yellow-tint border-2 border-t-email-blue flex items-center justify-center">
									<svg
										className="w-4 h-4 text-t-email-blue"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={3}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
							) : isStep2Active ? (
								/* Active: Purple gradient with link icon */
								<div
									className="w-[30px] h-[30px] rounded-[8px] border-2 border-[#554bfd] flex items-center justify-center"
									style={{
										backgroundImage:
											'linear-gradient(-86.7546542701692deg, rgba(145, 138, 255, 1) 3.8923%, rgba(94, 84, 253, 1) 93.118%)'
									}}
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_4734_43899)">
											<path
												d="M5.00016 4.6665H4.66683C2.82588 4.6665 1.3335 6.15889 1.3335 7.99984C1.3335 9.84079 2.82588 11.3332 4.66683 11.3332H6.00016C7.84111 11.3332 9.3335 9.84079 9.3335 7.99984M11.0002 11.3332H11.3335C13.1744 11.3332 14.6668 9.84079 14.6668 7.99984C14.6668 6.15889 13.1744 4.6665 11.3335 4.6665H10.0002C8.15921 4.6665 6.66683 6.15889 6.66683 7.99984"
												stroke="white"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_4734_43899">
												<rect width="16" height="16" fill="white" />
											</clipPath>
										</defs>
									</svg>
								</div>
							) : (
								/* Inactive: White square with purple border and link icon */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-t-step-inactive border border-t-badge-border flex items-center justify-center">
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_4734_43397)">
											<path
												d="M4.99967 4.6665H4.66634C2.82539 4.6665 1.33301 6.15889 1.33301 7.99984C1.33301 9.84079 2.82539 11.3332 4.66634 11.3332H5.99967C7.84062 11.3332 9.33301 9.84079 9.33301 7.99984M10.9997 11.3332H11.333C13.174 11.3332 14.6663 9.84079 14.6663 7.99984C14.6663 6.15889 13.174 4.6665 11.333 4.6665H9.99967C8.15873 4.6665 6.66634 6.15889 6.66634 7.99984"
												style={{ stroke: 'var(--t-email-blue)' }}
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_4734_43397">
												<rect width="16" height="16" fill="white" />
											</clipPath>
										</defs>
									</svg>
								</div>
							)}
						</div>
						<div className="flex flex-col gap-[2px]">
							<p className="text-t-email-blue text-[12px] leading-[14px] font-bold">
								Step 02
							</p>
							<p
								className={`text-t-default font-normal text-[14px] leading-[16px] opacity-85 ${
									isStep2Done ? 'line-through' : ''
								}`}
							>
								Connect your <span className="font-bold">ad account</span>
							</p>
						</div>
					</div>

					{/* Step 03: Install your Tracking code */}
					<div className="flex items-center gap-3">
						<div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0">
							{isStep3Done ? (
								/* Completed: Yellow background with checkmark */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-b-yellow-tint border-2 border-t-email-blue flex items-center justify-center">
									<svg
										className="w-4 h-4 text-t-email-blue"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={3}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
							) : isStep3Active ? (
								/* Active: Purple gradient with route icon */
								<div
									className="w-[30px] h-[30px] rounded-[8px] border-2 border-[#554bfd] flex items-center justify-center"
									style={{
										backgroundImage:
											'linear-gradient(-86.7546542701692deg, rgba(145, 138, 255, 1) 3.8923%, rgba(94, 84, 253, 1) 93.118%)'
									}}
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_4734_46803)">
											<path
												d="M7.66683 3.3335H7.95643C9.98792 3.3335 11.0037 3.3335 11.3893 3.69836C11.7226 4.01374 11.8703 4.47835 11.7803 4.9283C11.6762 5.44884 10.8469 6.0354 9.18835 7.20852L6.47864 9.12514C4.8201 10.2983 3.99082 10.8848 3.88672 11.4054C3.79673 11.8553 3.94444 12.3199 4.27774 12.6353C4.66332 13.0002 5.67907 13.0002 7.71056 13.0002H8.3335M5.3335 3.3335C5.3335 4.43807 4.43807 5.3335 3.3335 5.3335C2.22893 5.3335 1.3335 4.43807 1.3335 3.3335C1.3335 2.22893 2.22893 1.3335 3.3335 1.3335C4.43807 1.3335 5.3335 2.22893 5.3335 3.3335ZM14.6668 12.6668C14.6668 13.7714 13.7714 14.6668 12.6668 14.6668C11.5623 14.6668 10.6668 13.7714 10.6668 12.6668C10.6668 11.5623 11.5623 10.6668 12.6668 10.6668C13.7714 10.6668 14.6668 11.5623 14.6668 12.6668Z"
												stroke="white"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_4734_46803">
												<rect width="16" height="16" fill="white" />
											</clipPath>
										</defs>
									</svg>
								</div>
							) : (
								/* Inactive: White square with purple border and route icon */
								<div className="w-[30px] h-[30px] rounded-[8px] bg-t-step-inactive border border-t-badge-border flex items-center justify-center">
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_4734_43823)">
											<path
												d="M7.66683 3.3335H7.95643C9.98792 3.3335 11.0037 3.3335 11.3893 3.69836C11.7226 4.01374 11.8703 4.47835 11.7803 4.9283C11.6762 5.44884 10.8469 6.0354 9.18835 7.20852L6.47864 9.12514C4.8201 10.2983 3.99082 10.8848 3.88672 11.4054C3.79673 11.8553 3.94444 12.3199 4.27774 12.6353C4.66332 13.0002 5.67907 13.0002 7.71056 13.0002H8.3335M5.3335 3.3335C5.3335 4.43807 4.43807 5.3335 3.3335 5.3335C2.22893 5.3335 1.3335 4.43807 1.3335 3.3335C1.3335 2.22893 2.22893 1.3335 3.3335 1.3335C4.43807 1.3335 5.3335 2.22893 5.3335 3.3335ZM14.6668 12.6668C14.6668 13.7714 13.7714 14.6668 12.6668 14.6668C11.5623 14.6668 10.6668 13.7714 10.6668 12.6668C10.6668 11.5623 11.5623 10.6668 12.6668 10.6668C13.7714 10.6668 14.6668 11.5623 14.6668 12.6668Z"
												style={{ stroke: 'var(--t-email-blue)' }}
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_4734_43823">
												<rect width="16" height="16" fill="white" />
											</clipPath>
										</defs>
									</svg>
								</div>
							)}
						</div>
						<div className="flex flex-col gap-[2px]">
							<p className="text-t-email-blue text-[12px] leading-[14px] font-bold">
								Step 03
							</p>
							<p className="text-t-default font-normal text-[14px] leading-[16px] opacity-85">
								Install your <span className="font-bold">Tracking code</span>
							</p>
						</div>
					</div>
					<div className="flex items-center justify-end gap-4 flex-auto">
						{isStep2Done && !isStep3Done && (
							<div className="flex items-center gap-0">
								<p className="text-[color:var(--t-ink-black)] text-[14px] leading-[20px]">
									<button
										onClick={handleOptOutTracking}
										disabled={isOptingOut}
										className="text-t-dark-blue text-[14px] leading-[20px] underline hover:text-t-email-blue disabled:opacity-50 disabled:cursor-not-allowed items-center gap-2"
									>
										{isOptingOut && <DotMatrix size="xs" />}I don't want to
										install the Tracking Code
									</button>
									<span className="text-[color:var(--t-ink-black)] text-[14px] leading-[20px]">
										{' '}
										(not recommended)
									</span>
								</p>
							</div>
						)}
						{showScheduleCall && (
							<GButton
								label="Finish setup with an expert"
								onClick={async () => {
									// Determine step based on setup progress
									let stepName = 'tracking_code' // default
									if (!isStep1Done) {
										stepName = 'protect_website'
									} else if (!isStep2Done) {
										stepName = 'ad_account'
									}
									trackPostHogEvent(
										PostHogEvent.ONBOARDING_CONTACT_SUPPORT_CTA,
										PostHogPath.ONBOARDING,
										{ step: stepName }
									)
									window.location.href = getOnboardingBookingUrl()
									await Onboarding.setupExpertMeetingClicked(account.id)
								}}
								className="bg-transparent border border-[color:var(--button-outline-border)] hover:bg-transparent text-t-default rounded-full py-2 px-4 font-medium text-[14px] leading-[20px] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
							/>
						)}
					</div>
				</div>
			</>
		</div>
	)
}
